<template>
  <div>
    <div class="mb-4 text-subtitle-2">
      <span class="secondary-font font-weight-bold">{{
        $t("users.title")
      }}</span>
    </div>
    <v-card class="elevation-0" color="rgba(255,255,255,0)">
      <v-card-actions class="px-0 mb-2">
        <v-btn
          depressed
          @click="refreshItems()"
          :disabled="loading.filter"
          color="grey lighten-2"
          :loading="loading.refresh"
        >
          <v-icon left>mdi-refresh</v-icon>
          {{ $t("btn.refresh") }}
        </v-btn>
        <v-btn
          @click="getFilter()"
          depressed
          :loading="loading.filter"
          color="grey lighten-2"
          class="mr-2"
        >
          <v-icon left>mdi-filter-variant</v-icon>
          {{ $t("btn.filter") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" class="mr-2" :to="{ name: 'user-add' }">
          <v-icon left>mdi-account-plus-outline</v-icon>
          {{ $t("btn.add") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card class="elevation-1">
      <v-card-title>
        <span class="text-body-1">
          <span class="secondary-font font-weight-bold">{{
            $t("users.list.title")
          }}</span>
        </span>
      </v-card-title>
      <v-divider></v-divider>
      <v-data-table
        :headers="headers"
        :items="items"
        :server-items-length="meta.total"
        :loading="loading.list"
        :items-per-page="itemPerPage"
        :options.sync="pagination"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :footer-props="footerProps"
      >
        <template v-slot:body="{ items }" v-if="!_.isEmpty(items)">
          <tbody>
            <template v-for="item in items">
              <v-hover :key="item.id" v-slot:default="{ hover }">
                <tr>
                  <td style="min-width: 160px">
                    {{ $moment(item.created).format("YYYY-MM-DD HH:mm:ss") }}
                  </td>
                  <td style="min-width: 160px">
                    <v-chip
                      color="grey lighten-2"
                      class="table-chips text-body-2"
                      >{{ item.username }}</v-chip
                    >
                  </td>
                  <td>
                    <v-chip
                      color="grey lighten-2"
                      class="table-chips text-body-2"
                      >{{ $t(`users.fields.role.${item.role}`) }}</v-chip
                    >
                  </td>
                  <td>
                    <v-chip
                      v-if="item.active"
                      color="success"
                      class="table-chips text-body-2"
                      >{{ $t("commons.fields.status.activated") }}</v-chip
                    >
                    <v-chip
                      v-else
                      color="error"
                      class="table-chips text-body-2"
                      >{{ $t("commons.fields.status.deactivated") }}</v-chip
                    >
                  </td>
                  <td>
                    <v-chip
                      v-if="item.mynetwork && item.mynetwork.data && item.mynetwork.data.connected"
                      color="success"
                      class="table-chips text-body-2"
                      >{{ $t("users.fields.mynetwork_status.connected") }}</v-chip
                    >
                    <v-chip
                      v-else
                      color="grey lighten-2"
                      class="table-chips text-body-2"
                      >{{ $t("users.fields.mynetwork_status.disconnected") }}</v-chip
                    >
                  </td>
                  <td>
                    {{ `${item.profile.first_name} ${item.profile.last_name}` }}
                  </td>
                  <td style="min-width: 160px">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          @click="getDetails(item)"
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          v-show="hover || $vuetify.breakpoint.mdAndDown"
                          class="mr-2"
                        >
                          <v-icon color="primary">mdi-eye-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("btn.details") }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          :to="{ name: 'user-edit', params: { id: item.id } }"
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          v-show="hover || $vuetify.breakpoint.mdAndDown"
                          class="mr-2"
                        >
                          <v-icon color="primary">mdi-pencil-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("btn.edit") }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          @click="deleteUser(item)"
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          v-show="hover || $vuetify.breakpoint.mdAndDown"
                          :loading="loading.delete && user.id === item.id"
                          class="mr-2"
                        >
                          <v-icon color="primary">mdi-delete-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("btn.delete") }}</span>
                    </v-tooltip>
                    <v-tooltip top v-if="item.mynetwork && item.mynetwork.identifier">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          @click="connectMyNetwork(item)"
                          x-small
                          icon
                          v-bind="attrs"
                          v-on="on"
                          v-show="hover || $vuetify.breakpoint.mdAndDown"
                        >
                          <v-icon color="primary">mdi-connection</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("users.mynetwork.connect.btn") }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </v-hover>
            </template>
          </tbody>
        </template>
      </v-data-table>
    </v-card>
    <user-details :dialog="dialog.details" :user="user"></user-details>
    <user-filter @filter="applyFilter" :dialog="dialog.filter"></user-filter>
    <user-delete-confirm
      @confirm="confirmDeleteUser()"
      :dialog="dialog.delete"
    ></user-delete-confirm>
    <my-network-connect
      :dialog="dialog.mynetwork"
      :user="user"
      @success="refreshItems()"
    ></my-network-connect>
  </div>
</template>

<script>
import UserFilter from "./filter.vue";
import UserDetails from "./details.vue";
import ListMixin from "./../../mixins/commons/list";
import UserDeleteConfirm from "./../common/confirm.vue";
import MyNetworkConnect from "./mynetwork-connect.vue";

export default {
  mixins: [ListMixin],
  data: () => ({
    url_items: "/api/users",
    dialog: {
      details: {
        display: false,
      },
      filter: {
        display: false,
      },
      delete: {
        display: false,
        title: null,
      },
      mynetwork: {
        display: false,
      },
    },
    user: {},
  }),
  methods: {
    getFilter() {
      this.dialog.filter.display = true;
    },
    setHeaders() {
      this.headers = [
        {
          text: this.$t("commons.fields.created.title"),
          align: "start",
          sortable: true,
          value: "created",
          width: 160,
        },
        {
          text: this.$t("security.fields.username.title"),
          align: "start",
          sortable: false,
          value: "username",
          width: 160,
        },
        {
          text: this.$t("users.fields.role.title"),
          align: "start",
          sortable: false,
          value: "role",
          width: 90,
        },
        {
          text: this.$t("commons.fields.status.title"),
          align: "start",
          sortable: false,
          value: "active",
          width: 80,
        },
        {
          text: this.$t("users.fields.mynetwork_status.title"),
          align: "start",
          sortable: false,
          value: null,
          width: 120,
        },
        {
          text: this.$t("users.fields.fullname.title"),
          align: "start",
          sortable: false,
          value: null,
        },
        {
          text: "",
          align: "start",
          sortable: false,
          value: null,
          width: 160,
        },
      ];
    },
    getDetails(user) {
      this.user = user;
      this.dialog.details.display = true;
    },
    deleteUser(user) {
      this.user = user;
      this.dialog.delete.display = true;
      this.dialog.delete.title = this.$t("users.delete.confirm");
    },
    connectMyNetwork(user) {
      this.user = user;
      this.dialog.mynetwork.display = true;
    },
    async confirmDeleteUser() {
      try {
        await this.request({
          url: `/api/users/${this.user.id}`,
          method: "DELETE",
        });
        this.notify({ message: this.$t("users.delete.success") });

        this.refreshItems();
      } catch (error) {
        let err = "errors.500E";
        const status = error?.response.status;
        const content = error?.response.data;
        console.log(content);
        if (status === 422) {
          if (content.code === "P101") {
            err = "users.delete.errors.P101";
          } else if (content.code === "P102") {
            err = "users.delete.errors.P101";
          }
        }

        this.notify({ message: this.$t(err) });
      }
    },
  },
  components: {
    UserDetails,
    UserFilter,
    UserDeleteConfirm,
    MyNetworkConnect,
  },
};
</script>
